/**
 * Limpeza centralizada de estado de autenticação e caches relacionados.
 *
 * Objetivo:
 * - Evitar vazamento de contexto entre usuários após logout/login
 * - Remover impersonação e preferências dependentes de usuário/role
 * - Limpar caches offline e dados de pedido/export
 */

import { TokenService } from '../tokenService';
import { STORAGE_KEYS } from '../storage/constants';
import { Keys } from '../storage/keys';
// NOTE: OfflineCacheService importado dinamicamente para evitar core → pedidos circular

function safeRemove(storage: Storage, key: string) {
  try { storage.removeItem(key); } catch {}
}

function clearImpersonationKeys() {
  try {
    safeRemove(localStorage, STORAGE_KEYS.IMPERSONATE_ROLE);
    safeRemove(localStorage, STORAGE_KEYS.LEGACY.IMPERSONATE_ROLE);
  } catch {}
}

function clearKnownKeys() {
  // Chaves novas (session/local)
  safeRemove(sessionStorage, Keys.order.currentPedido);
  safeRemove(localStorage, Keys.order.currentPedido);

  safeRemove(sessionStorage, Keys.export.currentSaveData);
  safeRemove(localStorage, Keys.export.currentSaveData);

  // Aliases legados com chaves cruas
  safeRemove(sessionStorage, 'currentPedido');
  safeRemove(localStorage, 'currentPedido');
  safeRemove(sessionStorage, 'currentSaveData');
  safeRemove(localStorage, 'currentSaveData');

  // UI auxiliares
  safeRemove(localStorage, STORAGE_KEYS.EXPORT_CONFIG);
  safeRemove(localStorage, STORAGE_KEYS.LEGACY.EXPORT_CONFIG);
}

function clearByPrefixes() {
  const ls = localStorage;
  const ss = sessionStorage;
  const prefixes = [
    'cdr:export:',
    'cdr:order:',
    'cdr:prefs:',
    'cdr:corel:',
    'cdr:tools_sso:',
    'cdr:ui:'
  ];

  try {
    for (let i = ls.length - 1; i >= 0; i--) {
      const key = ls.key(i);
      if (!key) continue;
      if (prefixes.some(p => key.startsWith(p))) {
        safeRemove(ls, key);
      }
    }
  } catch {}

  try {
    for (let i = ss.length - 1; i >= 0; i--) {
      const key = ss.key(i);
      if (!key) continue;
      if (prefixes.some(p => key.startsWith(p))) {
        safeRemove(ss, key);
      }
    }
  } catch {}
}

async function clearOfflineCaches() {
  try {
    // Serviço dedicado da página de pedidos (dynamic import para evitar circular core → pedidos)
    const { OfflineCacheService } = await import('../../components/PedidosPage/services/offlineCacheService');
    OfflineCacheService.getInstance().clear();
  } catch {}
  try {
    // Garantia adicional: remover a chave crua usada pelo serviço offline
    safeRemove(localStorage, 'pedidos_offline_cache');
  } catch {}
}

/**
 * Limpa todos os artefatos de autenticação e caches de aplicação.
 * Use no logout (e opcionalmente antes de um novo login).
 */
export function clearAllAuthStateAndCaches() {
  // 1) Limpar tokens e dados do usuário (inclui chaves novas/legadas de auth)
  try { TokenService.clearTokens(); } catch {}

  // 2) Impersonação
  clearImpersonationKeys();

  // 3) Caches e dados dependentes de usuário/role
  clearKnownKeys();
  clearByPrefixes();
  clearOfflineCaches();
}

/**
 * Limpa apenas chaves de impersonação. Chame no início de um novo login.
 */
export function clearImpersonationOnly() {
  clearImpersonationKeys();
}


